import { useEffect, useState, useCallback } from 'react';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';

type Submission = {
  id: number;
  name: string;
  email: string;
  company: string | null;
  phone: string | null;
  message: string;
  file_name: string | null;
  file_mime: string | null;
  created_at: string;
};

type AdminUser = {
  id: number;
  name: string;
  created_at: string;
};

type SessionInfo = {
  name: string;
  isMaster: boolean;
};

type Application = {
  id: number;
  legal_name: string;
  dba: string | null;
  business_type: string | null;
  monthly_volume: string | null;
  avg_ticket: string | null;
  owner_first: string | null;
  owner_last: string | null;
  owner_email: string | null;
  owner_phone: string | null;
  status: string;
  created_at: string;
};

type ApplicationDetail = Application & {
  ein: string | null;
  date_established: string | null;
  state_of_inc: string | null;
  website: string | null;
  business_phone: string | null;
  business_street: string | null;
  business_city: string | null;
  business_state: string | null;
  business_zip: string | null;
  owner_title: string | null;
  owner_dob: string | null;
  owner_ssn: string | null;
  owner_pct: string | null;
  owner_street: string | null;
  owner_city: string | null;
  owner_state: string | null;
  owner_zip: string | null;
  products_services: string | null;
  high_ticket: string | null;
  pct_card_present: string | null;
  pct_ecommerce: string | null;
  pct_moto: string | null;
  current_processor: string | null;
  reason_switching: string | null;
  bank_name: string | null;
  routing_number: string | null;
  account_number: string | null;
  account_type: string | null;
  signature: string | null;
};

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  reviewing: 'bg-blue-50 text-blue-700 border-blue-200',
  approved: 'bg-green-50 text-green-700 border-green-200',
  declined: 'bg-red-50 text-red-600 border-red-200',
};

const TOKEN_STORAGE_KEY = 'fc_admin_token';

export default function AdminContacts() {
  const [token, setToken] = useState<string>(() => {
    if (typeof window === 'undefined') return '';
    return window.localStorage.getItem(TOKEN_STORAGE_KEY) || '';
  });
  const [tokenInput, setTokenInput] = useState('');
  const [authError, setAuthError] = useState('');
  const [session, setSession] = useState<SessionInfo | null>(null);

  const [activeTab, setActiveTab] = useState<'contacts' | 'applications' | 'users'>('contacts');

  // --- Applications state ---
  const [appsLoading, setAppsLoading] = useState(false);
  const [appsError, setAppsError] = useState('');
  const [applications, setApplications] = useState<Application[]>([]);
  const [appsTotal, setAppsTotal] = useState(0);
  const [appsQuery, setAppsQuery] = useState('');
  const [expandedApp, setExpandedApp] = useState<Record<number, ApplicationDetail | null>>({});
  const [loadingApp, setLoadingApp] = useState<Record<number, boolean>>({});

  // --- Contacts state ---
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [total, setTotal] = useState(0);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  // --- Users state ---
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [newUserName, setNewUserName] = useState('');
  const [createdToken, setCreatedToken] = useState<{ name: string; token: string } | null>(null);
  const [addingUser, setAddingUser] = useState(false);
  const [copied, setCopied] = useState(false);

  // Load session info when token changes
  useEffect(() => {
    if (!token) { setSession(null); return; }
    fetch('/api/admin/me', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { if (d.ok) setSession({ name: d.name, isMaster: d.isMaster }); })
      .catch(() => {});
  }, [token]);

  const load = useCallback(
    async (currentToken: string, q: string) => {
      setLoading(true);
      setError('');
      try {
        const url = `/api/admin/contacts${q ? `?q=${encodeURIComponent(q)}` : ''}`;
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${currentToken}` },
        });
        if (res.status === 401) {
          setError('Invalid access token. Please sign in again.');
          setToken('');
          window.localStorage.removeItem(TOKEN_STORAGE_KEY);
          return;
        }
        const data = await res.json();
        if (!res.ok || !data.ok) {
          setError(data.error || 'Failed to load submissions.');
          return;
        }
        setSubmissions(data.submissions);
        setTotal(data.total);
      } catch {
        setError('Failed to load submissions.');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const loadUsers = useCallback(async (currentToken: string) => {
    setUsersLoading(true);
    setUsersError('');
    try {
      const res = await fetch('/api/admin/users', {
        headers: { Authorization: `Bearer ${currentToken}` },
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        setUsersError(data.error || 'Failed to load users.');
        return;
      }
      setUsers(data.users);
    } catch {
      setUsersError('Failed to load users.');
    } finally {
      setUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token, '');
  }, [token, load]);

  useEffect(() => {
    if (!token) return;
    const t = setTimeout(() => load(token, query), 300);
    return () => clearTimeout(t);
  }, [query, token, load]);

  const loadApplications = useCallback(async (currentToken: string, q: string) => {
    setAppsLoading(true);
    setAppsError('');
    try {
      const url = `/api/admin/applications${q ? `?q=${encodeURIComponent(q)}` : ''}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${currentToken}` } });
      const data = await res.json();
      if (!res.ok || !data.ok) { setAppsError(data.error || 'Failed to load applications.'); return; }
      setApplications(data.applications);
      setAppsTotal(data.total);
    } catch { setAppsError('Failed to load applications.'); }
    finally { setAppsLoading(false); }
  }, []);

  const expandApplication = async (id: number) => {
    if (expandedApp[id] !== undefined) {
      setExpandedApp(prev => { const n = { ...prev }; delete n[id]; return n; });
      return;
    }
    setLoadingApp(prev => ({ ...prev, [id]: true }));
    try {
      const res = await fetch(`/api/admin/applications/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (res.ok && data.ok) setExpandedApp(prev => ({ ...prev, [id]: data.application }));
    } finally { setLoadingApp(prev => ({ ...prev, [id]: false })); }
  };

  const updateAppStatus = async (id: number, status: string) => {
    const res = await fetch(`/api/admin/applications/${id}/status`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    if (res.ok) {
      setApplications(prev => prev.map(a => a.id === id ? { ...a, status } : a));
      setExpandedApp(prev => prev[id] ? { ...prev, [id]: { ...prev[id]!, status } } : prev);
    }
  };

  useEffect(() => {
    if (token && activeTab === 'users' && session?.isMaster) loadUsers(token);
  }, [activeTab, token, session, loadUsers]);

  useEffect(() => {
    if (token && activeTab === 'applications') loadApplications(token, '');
  }, [activeTab, token, loadApplications]);

  useEffect(() => {
    if (!token || activeTab !== 'applications') return;
    const t = setTimeout(() => loadApplications(token, appsQuery), 300);
    return () => clearTimeout(t);
  }, [appsQuery, token, activeTab, loadApplications]);

  const handleSignIn = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmed = tokenInput.trim();
    if (!trimmed) { setAuthError('Please enter your access token.'); return; }
    window.localStorage.setItem(TOKEN_STORAGE_KEY, trimmed);
    setToken(trimmed);
    setTokenInput('');
    setAuthError('');
  };

  const handleSignOut = () => {
    window.localStorage.removeItem(TOKEN_STORAGE_KEY);
    setToken('');
    setSubmissions([]);
    setTotal(0);
    setQuery('');
    setSession(null);
    setUsers([]);
    setCreatedToken(null);
  };

  const downloadFile = async (id: number, fileName: string) => {
    const res = await fetch(`/api/admin/contacts/${id}/file`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleAddUser = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const name = newUserName.trim();
    if (!name) return;
    setAddingUser(true);
    try {
      const res = await fetch('/api/admin/users', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        setUsersError(data.error || 'Failed to create user.');
        return;
      }
      setCreatedToken({ name: data.user.name, token: data.token });
      setNewUserName('');
      loadUsers(token);
    } catch {
      setUsersError('Failed to create user.');
    } finally {
      setAddingUser(false);
    }
  };

  const handleDeleteUser = async (id: number) => {
    if (!confirm('Remove this user? Their token will stop working immediately.')) return;
    try {
      const res = await fetch(`/api/admin/users/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        setUsersError(data.error || 'Failed to remove user.');
        return;
      }
      setUsers(prev => prev.filter(u => u.id !== id));
    } catch {
      setUsersError('Failed to remove user.');
    }
  };

  const copyToken = (t: string) => {
    navigator.clipboard.writeText(t).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#F9F9F7]" style={{ fontFamily: "'Google Sans Flex', sans-serif" }}>
        <Navbar />
        <main className="max-w-md mx-auto px-5 py-32">
          <p className="text-[#a8894a] text-[10px] uppercase tracking-[0.25em] font-medium mb-5">Admin Access</p>
          <h1 className="text-[#061e45] text-3xl font-light mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>
            Sign in to view submissions.
          </h1>
          <form onSubmit={handleSignIn} className="flex flex-col gap-5">
            <div>
              <label htmlFor="token" className="block text-[#1A1A1A]/60 text-[11px] uppercase tracking-[0.15em] font-medium mb-2">
                Access Token
              </label>
              <input
                id="token"
                type="password"
                autoComplete="off"
                value={tokenInput}
                onChange={(e) => setTokenInput(e.target.value)}
                className="w-full bg-white border border-[#1A1A1A]/10 text-[#1A1A1A] px-4 py-3.5 text-sm outline-none focus:border-[#a8894a] transition-colors duration-200"
                placeholder="Paste your admin token"
              />
            </div>
            {authError && <p className="text-red-500 text-sm">{authError}</p>}
            <button
              type="submit"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#d3b366] text-[#061e45] text-[12px] uppercase tracking-[0.18em] font-semibold hover:bg-[#a8894a] hover:text-white transition-colors duration-200 cursor-pointer self-start"
            >
              Sign In
            </button>
          </form>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7]" style={{ fontFamily: "'Google Sans Flex', sans-serif" }}>
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-5 md:px-8 py-20">

        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
          <div>
            <p className="text-[#a8894a] text-[10px] uppercase tracking-[0.25em] font-medium mb-3">Admin</p>
            <h1 className="text-[#061e45] text-3xl md:text-4xl font-light" style={{ fontFamily: "'Playfair Display', serif" }}>
              {session?.name ? `Welcome, ${session.name}` : 'Admin Panel'}
            </h1>
            {session?.isMaster && (
              <p className="text-[#a8894a] text-[11px] uppercase tracking-[0.15em] mt-1">Master Access</p>
            )}
          </div>
          <button
            onClick={handleSignOut}
            className="px-5 py-3 border border-[#1A1A1A]/15 text-[#1A1A1A]/70 text-[11px] uppercase tracking-[0.15em] font-medium hover:border-[#a8894a] hover:text-[#a8894a] transition-colors duration-200"
          >
            Sign Out
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-0 border-b border-[#1A1A1A]/10 mb-8">
          {[
            { key: 'contacts', label: `Submissions${total ? ` (${total})` : ''}` },
            { key: 'applications', label: `Applications${appsTotal ? ` (${appsTotal})` : ''}` },
            ...(session?.isMaster ? [{ key: 'users', label: 'Users' }] : []),
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as 'contacts' | 'applications' | 'users')}
              className={`px-6 py-3 text-[11px] uppercase tracking-[0.18em] font-medium transition-colors duration-200 cursor-pointer border-b-2 -mb-px ${
                activeTab === tab.key
                  ? 'border-[#d3b366] text-[#061e45]'
                  : 'border-transparent text-[#1A1A1A]/45 hover:text-[#061e45]'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Contacts Tab ── */}
        {activeTab === 'contacts' && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div>
                <label htmlFor="search" className="block text-[#1A1A1A]/60 text-[11px] uppercase tracking-[0.15em] font-medium mb-2">
                  Search
                </label>
                <input
                  id="search"
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Name, email, company, message…"
                  className="w-72 bg-white border border-[#1A1A1A]/10 text-[#1A1A1A] px-4 py-3 text-sm outline-none focus:border-[#a8894a] transition-colors duration-200"
                />
              </div>
            </div>

            {error && <p className="text-red-500 text-sm mb-6">{error}</p>}

            <div className="bg-white border border-[#1A1A1A]/8">
              {loading && submissions.length === 0 ? (
                <div className="p-12 text-center text-[#1A1A1A]/55 text-sm">Loading submissions…</div>
              ) : submissions.length === 0 ? (
                <div className="p-12 text-center text-[#1A1A1A]/55 text-sm">
                  {query ? 'No submissions match your search.' : 'No submissions yet.'}
                </div>
              ) : (
                <div className="divide-y divide-[#1A1A1A]/8">
                  {submissions.map((s) => {
                    const isOpen = !!expanded[s.id];
                    return (
                      <div key={s.id} className="p-6">
                        <button
                          onClick={() => setExpanded(prev => ({ ...prev, [s.id]: !prev[s.id] }))}
                          className="w-full flex flex-wrap items-start justify-between gap-4 text-left cursor-pointer"
                        >
                          <div className="flex-1 min-w-[240px]">
                            <div className="flex items-center gap-3 mb-1">
                              <span className="text-[#061e45] font-medium">{s.name}</span>
                              <span className="text-[#1A1A1A]/40 text-xs">#{s.id}</span>
                              {s.file_name && (
                                <span className="text-[10px] uppercase tracking-[0.1em] text-[#a8894a] border border-[#d3b366]/40 px-2 py-0.5">
                                  attachment
                                </span>
                              )}
                            </div>
                            <div className="text-[#1A1A1A]/65 text-sm">
                              <a href={`mailto:${s.email}`} className="hover:text-[#a8894a]" onClick={e => e.stopPropagation()}>
                                {s.email}
                              </a>
                              {s.company ? <span className="text-[#1A1A1A]/40"> · {s.company}</span> : null}
                              {s.phone ? <span className="text-[#1A1A1A]/40"> · {s.phone}</span> : null}
                            </div>
                            {!isOpen && (
                              <p className="text-[#1A1A1A]/55 text-sm mt-2 line-clamp-1">{s.message}</p>
                            )}
                          </div>
                          <div className="text-right text-[#1A1A1A]/45 text-xs whitespace-nowrap pt-1">
                            {formatDate(s.created_at)}
                          </div>
                        </button>
                        {isOpen && (
                          <div className="mt-4 flex flex-col gap-3">
                            <div className="p-5 bg-[#F9F9F7] border-l-2 border-[#d3b366] text-[#1A1A1A]/80 text-sm leading-relaxed whitespace-pre-wrap">
                              {s.message || <span className="text-[#1A1A1A]/35 italic">No message provided.</span>}
                            </div>
                            {s.file_name && (
                              <button
                                onClick={() => downloadFile(s.id, s.file_name!)}
                                className="self-start inline-flex items-center gap-2 px-4 py-2 border border-[#d3b366]/50 text-[#a8894a] text-[12px] uppercase tracking-[0.12em] font-medium hover:bg-[#d3b366]/10 transition-colors duration-200"
                              >
                                <i className="ri-download-2-line" />
                                Download: {s.file_name}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}

        {/* ── Applications Tab ── */}
        {activeTab === 'applications' && (
          <>
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <div>
                <label className="block text-[#1A1A1A]/60 text-[11px] uppercase tracking-[0.15em] font-medium mb-2">Search</label>
                <input
                  type="text"
                  value={appsQuery}
                  onChange={e => setAppsQuery(e.target.value)}
                  placeholder="Business name, owner, email…"
                  className="w-72 bg-white border border-[#1A1A1A]/10 text-[#1A1A1A] px-4 py-3 text-sm outline-none focus:border-[#a8894a] transition-colors duration-200"
                />
              </div>
            </div>
            {appsError && <p className="text-red-500 text-sm mb-4">{appsError}</p>}
            <div className="bg-white border border-[#1A1A1A]/8">
              {appsLoading && applications.length === 0 ? (
                <div className="p-12 text-center text-[#1A1A1A]/55 text-sm">Loading applications…</div>
              ) : applications.length === 0 ? (
                <div className="p-12 text-center text-[#1A1A1A]/55 text-sm">
                  {appsQuery ? 'No applications match your search.' : 'No applications yet.'}
                </div>
              ) : (
                <div className="divide-y divide-[#1A1A1A]/8">
                  {applications.map(app => {
                    const detail = expandedApp[app.id];
                    const isOpen = detail !== undefined;
                    const isLoading = loadingApp[app.id];
                    return (
                      <div key={app.id} className="p-6">
                        <button
                          onClick={() => expandApplication(app.id)}
                          className="w-full flex flex-wrap items-start justify-between gap-4 text-left cursor-pointer"
                        >
                          <div className="flex-1 min-w-[240px]">
                            <div className="flex flex-wrap items-center gap-3 mb-1">
                              <span className="text-[#061e45] font-medium">{app.legal_name}</span>
                              {app.dba && <span className="text-[#1A1A1A]/40 text-xs">dba {app.dba}</span>}
                              <span className="text-[#1A1A1A]/40 text-xs">#{app.id}</span>
                              <span className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 border ${STATUS_COLORS[app.status] || STATUS_COLORS.pending}`}>
                                {app.status}
                              </span>
                            </div>
                            <div className="text-[#1A1A1A]/60 text-sm">
                              {app.owner_first} {app.owner_last}
                              {app.owner_email && <> · <a href={`mailto:${app.owner_email}`} className="hover:text-[#a8894a]" onClick={e => e.stopPropagation()}>{app.owner_email}</a></>}
                            </div>
                            <div className="text-[#1A1A1A]/40 text-xs mt-1">
                              {app.business_type}{app.monthly_volume ? ` · ${app.monthly_volume}/mo` : ''}{app.avg_ticket ? ` · avg $${app.avg_ticket}` : ''}
                            </div>
                          </div>
                          <div className="text-right text-[#1A1A1A]/45 text-xs whitespace-nowrap pt-1">
                            {formatDate(app.created_at)}
                          </div>
                        </button>

                        {isLoading && <div className="mt-4 text-sm text-[#1A1A1A]/45">Loading…</div>}

                        {isOpen && detail && (
                          <div className="mt-5 border-t border-[#1A1A1A]/8 pt-5">
                            {/* Status update */}
                            <div className="flex flex-wrap items-center gap-3 mb-6">
                              <span className="text-[11px] uppercase tracking-[0.12em] text-[#1A1A1A]/50">Status:</span>
                              {['pending', 'reviewing', 'approved', 'declined'].map(s => (
                                <button
                                  key={s}
                                  onClick={() => updateAppStatus(app.id, s)}
                                  className={`px-3 py-1 text-[10px] uppercase tracking-[0.12em] font-medium border transition-colors duration-150 cursor-pointer ${
                                    detail.status === s
                                      ? STATUS_COLORS[s]
                                      : 'border-[#1A1A1A]/15 text-[#1A1A1A]/40 hover:border-[#1A1A1A]/30'
                                  }`}
                                >
                                  {s}
                                </button>
                              ))}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 text-sm">
                              <div className="flex flex-col gap-2">
                                <p className="text-[10px] uppercase tracking-[0.18em] text-[#a8894a] font-medium mb-1">Business</p>
                                {detail.ein && <div><span className="text-[#1A1A1A]/45 text-xs">EIN</span><br /><span className="text-[#061e45]">{detail.ein}</span></div>}
                                {detail.business_type && <div><span className="text-[#1A1A1A]/45 text-xs">Type</span><br /><span className="text-[#061e45]">{detail.business_type}</span></div>}
                                {detail.date_established && <div><span className="text-[#1A1A1A]/45 text-xs">Established</span><br /><span className="text-[#061e45]">{detail.date_established}</span></div>}
                                {detail.state_of_inc && <div><span className="text-[#1A1A1A]/45 text-xs">State of Inc.</span><br /><span className="text-[#061e45]">{detail.state_of_inc}</span></div>}
                                {detail.website && <div><span className="text-[#1A1A1A]/45 text-xs">Website</span><br /><a href={detail.website} target="_blank" rel="noopener noreferrer" className="text-[#061e45] hover:text-[#a8894a]">{detail.website}</a></div>}
                                {detail.business_phone && <div><span className="text-[#1A1A1A]/45 text-xs">Phone</span><br /><span className="text-[#061e45]">{detail.business_phone}</span></div>}
                                {(detail.business_street) && <div><span className="text-[#1A1A1A]/45 text-xs">Address</span><br /><span className="text-[#061e45]">{[detail.business_street, detail.business_city, detail.business_state, detail.business_zip].filter(Boolean).join(', ')}</span></div>}
                              </div>
                              <div className="flex flex-col gap-2">
                                <p className="text-[10px] uppercase tracking-[0.18em] text-[#a8894a] font-medium mb-1">Principal</p>
                                <div><span className="text-[#1A1A1A]/45 text-xs">Name</span><br /><span className="text-[#061e45]">{detail.owner_first} {detail.owner_last}</span></div>
                                {detail.owner_title && <div><span className="text-[#1A1A1A]/45 text-xs">Title</span><br /><span className="text-[#061e45]">{detail.owner_title}</span></div>}
                                {detail.owner_pct && <div><span className="text-[#1A1A1A]/45 text-xs">Ownership</span><br /><span className="text-[#061e45]">{detail.owner_pct}%</span></div>}
                                {detail.owner_dob && <div><span className="text-[#1A1A1A]/45 text-xs">DOB</span><br /><span className="text-[#061e45]">{detail.owner_dob}</span></div>}
                                {detail.owner_ssn && <div><span className="text-[#1A1A1A]/45 text-xs">SSN</span><br /><span className="text-[#061e45]">***-**-{detail.owner_ssn.slice(-4)}</span></div>}
                                {detail.owner_email && <div><span className="text-[#1A1A1A]/45 text-xs">Email</span><br /><a href={`mailto:${detail.owner_email}`} className="text-[#061e45] hover:text-[#a8894a]">{detail.owner_email}</a></div>}
                                {detail.owner_phone && <div><span className="text-[#1A1A1A]/45 text-xs">Phone</span><br /><span className="text-[#061e45]">{detail.owner_phone}</span></div>}
                                {detail.owner_street && <div><span className="text-[#1A1A1A]/45 text-xs">Home Address</span><br /><span className="text-[#061e45]">{[detail.owner_street, detail.owner_city, detail.owner_state, detail.owner_zip].filter(Boolean).join(', ')}</span></div>}
                              </div>
                              <div className="flex flex-col gap-2">
                                <p className="text-[10px] uppercase tracking-[0.18em] text-[#a8894a] font-medium mb-1">Processing</p>
                                {detail.monthly_volume && <div><span className="text-[#1A1A1A]/45 text-xs">Monthly Volume</span><br /><span className="text-[#061e45]">{detail.monthly_volume}</span></div>}
                                {detail.avg_ticket && <div><span className="text-[#1A1A1A]/45 text-xs">Avg Ticket</span><br /><span className="text-[#061e45]">${detail.avg_ticket}</span></div>}
                                {detail.high_ticket && <div><span className="text-[#1A1A1A]/45 text-xs">High Ticket</span><br /><span className="text-[#061e45]">${detail.high_ticket}</span></div>}
                                {(detail.pct_card_present || detail.pct_ecommerce || detail.pct_moto) && (
                                  <div><span className="text-[#1A1A1A]/45 text-xs">Processing Mix</span><br /><span className="text-[#061e45]">CP {detail.pct_card_present}% · EC {detail.pct_ecommerce}% · MOTO {detail.pct_moto}%</span></div>
                                )}
                                {detail.current_processor && <div><span className="text-[#1A1A1A]/45 text-xs">Current Processor</span><br /><span className="text-[#061e45]">{detail.current_processor}</span></div>}
                                {detail.products_services && <div><span className="text-[#1A1A1A]/45 text-xs">Products / Services</span><br /><span className="text-[#061e45]">{detail.products_services}</span></div>}
                                <p className="text-[10px] uppercase tracking-[0.18em] text-[#a8894a] font-medium mt-2 mb-1">Banking</p>
                                {detail.bank_name && <div><span className="text-[#1A1A1A]/45 text-xs">Bank</span><br /><span className="text-[#061e45]">{detail.bank_name} · {detail.account_type}</span></div>}
                                {detail.routing_number && <div><span className="text-[#1A1A1A]/45 text-xs">Routing</span><br /><span className="text-[#061e45]">{detail.routing_number}</span></div>}
                                {detail.account_number && <div><span className="text-[#1A1A1A]/45 text-xs">Account</span><br /><span className="text-[#061e45]">****{detail.account_number.slice(-4)}</span></div>}
                              </div>
                            </div>
                            {detail.signature && (
                              <div className="mt-5 pt-4 border-t border-[#1A1A1A]/8">
                                <span className="text-[#1A1A1A]/45 text-xs uppercase tracking-[0.1em]">Electronic Signature</span>
                                <p className="text-[#061e45] italic mt-1" style={{ fontFamily: 'Georgia, serif', fontSize: '16px' }}>{detail.signature}</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}

        {/* ── Users Tab (master only) ── */}
        {activeTab === 'users' && session?.isMaster && (
          <div className="flex flex-col gap-10">

            {/* New token reveal */}
            {createdToken && (
              <div className="p-6 border border-[#d3b366]/40 bg-[#d3b366]/5">
                <p className="text-[#a8894a] text-[10px] uppercase tracking-[0.2em] font-medium mb-2">
                  Token created for {createdToken.name} — copy it now, it won't be shown again
                </p>
                <div className="flex flex-wrap items-center gap-3 mt-3">
                  <code className="flex-1 min-w-0 bg-white border border-[#1A1A1A]/10 px-4 py-3 text-sm text-[#061e45] font-mono break-all">
                    {createdToken.token}
                  </code>
                  <button
                    onClick={() => copyToken(createdToken.token)}
                    className="shrink-0 inline-flex items-center gap-2 px-5 py-3 bg-[#d3b366] text-[#061e45] text-[11px] uppercase tracking-[0.15em] font-semibold hover:bg-[#a8894a] hover:text-white transition-colors duration-200"
                  >
                    <i className={copied ? 'ri-check-line' : 'ri-clipboard-line'} />
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                  <button
                    onClick={() => setCreatedToken(null)}
                    className="shrink-0 text-[#1A1A1A]/40 hover:text-[#1A1A1A]/70 text-lg"
                    aria-label="Dismiss"
                  >
                    <i className="ri-close-line" />
                  </button>
                </div>
              </div>
            )}

            {/* Add user form */}
            <div className="bg-white border border-[#1A1A1A]/8 p-8">
              <h2 className="text-[#061e45] text-xl font-light mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                Add a New User
              </h2>
              <form onSubmit={handleAddUser} className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-[#1A1A1A]/60 text-[11px] uppercase tracking-[0.15em] font-medium mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    value={newUserName}
                    onChange={e => setNewUserName(e.target.value)}
                    placeholder="e.g. Sarah"
                    className="w-full bg-[#F9F9F7] border border-[#1A1A1A]/10 text-[#1A1A1A] px-4 py-3 text-sm outline-none focus:border-[#a8894a] transition-colors duration-200"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="submit"
                    disabled={addingUser || !newUserName.trim()}
                    className="inline-flex items-center gap-2 px-7 py-3 bg-[#061e45] text-white text-[11px] uppercase tracking-[0.18em] font-semibold hover:bg-[#1a3870] transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                  >
                    {addingUser ? 'Creating…' : 'Create Token'}
                  </button>
                </div>
              </form>
            </div>

            {usersError && <p className="text-red-500 text-sm">{usersError}</p>}

            {/* Users list */}
            <div className="bg-white border border-[#1A1A1A]/8">
              <div className="px-6 py-4 border-b border-[#1A1A1A]/8">
                <p className="text-[#1A1A1A]/55 text-sm">{users.length} user{users.length === 1 ? '' : 's'}</p>
              </div>
              {usersLoading ? (
                <div className="p-10 text-center text-[#1A1A1A]/55 text-sm">Loading…</div>
              ) : users.length === 0 ? (
                <div className="p-10 text-center text-[#1A1A1A]/55 text-sm">No users yet. Add one above.</div>
              ) : (
                <div className="divide-y divide-[#1A1A1A]/8">
                  {users.map(u => (
                    <div key={u.id} className="px-6 py-5 flex items-center justify-between gap-4">
                      <div>
                        <p className="text-[#061e45] font-medium">{u.name}</p>
                        <p className="text-[#1A1A1A]/45 text-xs mt-0.5">Added {formatDate(u.created_at)}</p>
                      </div>
                      <button
                        onClick={() => handleDeleteUser(u.id)}
                        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] font-medium text-red-400 hover:text-red-600 transition-colors duration-200 cursor-pointer"
                      >
                        <i className="ri-delete-bin-line" />
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        )}

      </main>
      <Footer />
    </div>
  );
}
